import {inject} from "@angular/core";
import {Auth, user} from "@angular/fire/auth";
import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot} from "@angular/router";
import {catchError, firstValueFrom, map, of, switchMap} from "rxjs";
import {AuthzService} from "./authz.service";
import {EventDataService} from "./event-data.service";
import {ToastService} from "./toast";

export const hasPermission: (permission: string) => CanActivateFn = (permission: string) =>
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router = inject(Router);
    const authzService = inject(AuthzService);
    const eventDataService = inject(EventDataService);
    const toast = inject(ToastService);
    const regionKey = route.paramMap.get('regionKey');
    const eventKey = route.paramMap.get('eventKey') || undefined;

    if(!regionKey) return false;

    return authzService.isAuthorized(regionKey, eventKey, permission).pipe(
      switchMap(async isAuthorized => {
        if (isAuthorized) return true;

        const name = await firstValueFrom(eventDataService.getEventOrRegionName(regionKey, eventKey));
        if(!name) {
          console.warn('Event not found', eventKey);
          return router.parseUrl(`/error?title=Event not found&message=Check if your URL is valid&url=${getResolvedUrl(route)}`);
        }

        if(eventKey) {
          toast.show('You need additional access to view that page');
          return router.parseUrl(`/region/${regionKey}/event/${eventKey}/request-access?url=${getResolvedUrl(route)}&permission=${permission}`);
        } else {
          return router.parseUrl(`/error?title=Not authorized&message=Contact a region administrator to get access&url=${getResolvedUrl(route)}`);
        }
      }),
      catchError(() => {
        return of(router.parseUrl(`/error?url=${getResolvedUrl(route)}`));
      }),
    );
  };

function getResolvedUrl(route: ActivatedRouteSnapshot): string {
  return route.pathFromRoot
    .map(v => v.url.map(segment => segment.toString()).join('/'))
    .join('/');
}

export const isLoggedIn: CanActivateFn =
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const auth = inject(Auth);
    const router = inject(Router);

    return user(auth).pipe(
      map(u => {
        if(u) return true;

        return router.parseUrl(`/login?redirect=${encodeURIComponent(state.url)}`);
      }),
    );
  };

export const isLoggedInNonAnon: CanActivateFn =
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const auth = inject(Auth);
    const router = inject(Router);

    return user(auth).pipe(
      map(u => {
        if(u && !u.isAnonymous) return true;

        return router.parseUrl(`/login?redirect=${encodeURIComponent(state.url)}&nonAnon=true`);
      }),
    );
  };

export const isSuperAdmin: CanActivateFn =
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const db = inject(AuthzService);

    return db.isSuperAdmin();
  };
